import { SalaryType } from '@sysarb/sysarb-types/general/enums';
import { InputFieldNames } from '@sysarb/sysarb-types-v2/services/v1/analytics';
import type { EqualWork, EqualWorkFilter, EqualWorkQueryOptions, InsightResponse, PossibleEqualWorkFilterValues, PossibleFilterDisplayStringCheckboxValues, PossibleFilterOrganisationNode } from '@sysarb/sysarb-types-v2/services/v1/pay-equity-audit';
import type { EqualWorkAnalyzisDataV2 } from '@sysarb/sysarb-types-v2/services/v1/pay-equity-audit';
import type { EmploymentHistoricalAnalysis, OrganizationEmploymentWorkInsightVariables, WorkHistoricalAnalysis, WorkTag } from '@sysarb/sysarb-types-v2/services/v1/pea';
import type { EmploymentDetailV4 } from '@sysarb/sysarb-types-v2/services/v1/pay-equity-audit/employment';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { transformWorkTagsToMap } from '@/composables/tags/useWorkTags';
import type { AnalyzeJobEmploymentV2, EmploymentHistoricalSalaryAdjustment } from '@sysarb/sysarb-types-v2/services/v1/pea/employment';
import type { Serializer } from '@vueuse/core';
import { useDomainStorage } from '@/composables/useDomainStorage';
import { isOrganizationLevelCheckboxFilterKey, withAllCheckboxFilterKeys } from '@/views/payEquityAudit/analyzeV2/guards';
import type { ToolbarMessagePopup } from './components/sidebar/types';
import { usePresentationModeStore } from '@/store/presentationModeStore';
import { ORGANIZATION_LEVEL_KEYS } from '@/views/payEquityAudit/analyzeV2/constants';
import type { CheckboxFilterItem, OrganizationLevelCheckboxFilterKey, SelectedFilterOptionHighlightMap, SelectedFilterOptionMap, CountAndHighlightCheckboxFilterKey, BackendCheckboxFilterKey, TreeFilterKey } from '@/views/payEquityAudit/analyzeV2/types';
import type { EqualJobsSortingGroup } from '@/views/payEquityAudit/analyzeV2/composables/useEqualJobAnalysis';

const STORE_ID = 'analyzeEqualWorkStore';

type AnalysisType = 'predicted-salary' | 'structure' | 'explore' | 'explanations-and-adjustments';
type SideBarType = 'analyze' | 'employment' | 'explore-settings' | 'structure';

type ChartType = 'scatter' | 'line';

type PredictedSalarySettings = {
    payRange: boolean;
    querySettings: EqualWorkQueryOptions;
};

type StructureSettings = {
    showGroupMeanSalaryLine: boolean;
    showGroupMedianSalaryLine: boolean;
    showBenchmark: boolean;
    showRegression: boolean;
    chartType: ChartType;
    querySettings: EqualWorkQueryOptions;
    payRange: boolean;
};

const PREDICTED_SALARY_SETTINGS_DEFAULT: PredictedSalarySettings = {
    payRange: false,
    querySettings: {
        groupBy: InputFieldNames.gender,
        simulated: false,
        salaryType: SalaryType.TotalSalary,
        xAxis: InputFieldNames.age,
    },
};

const STRUCTURE_AND_EXPLORE_SETTINGS_DEFAULT: StructureSettings = {
    showGroupMeanSalaryLine: false,
    showGroupMedianSalaryLine: false,
    showBenchmark: false,
    showRegression: false,
    payRange: true,
    chartType: 'scatter',
    querySettings: {
        groupBy: InputFieldNames.gender,
        xAxis: InputFieldNames.age,
        salaryType: SalaryType.TotalSalary,
        simulated: false,
    },
};

/**
 * Type used for persisting the selected filter option highlight map to session storage.
 */
type PersistedSelectedFilterOptionHighlightMap = Omit<SelectedFilterOptionHighlightMap, 'tree'> & {
    tree: {
        organisationNode: string[]
    },
};

const filterHighlightMapSeralizer: Serializer<SelectedFilterOptionHighlightMap> = {
    read: (value): SelectedFilterOptionHighlightMap => {
        const parsedValue: PersistedSelectedFilterOptionHighlightMap = JSON.parse(value);

        const newSelectedFilterHighlightMap: SelectedFilterOptionHighlightMap = {
            ...parsedValue,
            checkbox: withAllCheckboxFilterKeys(parsedValue.checkbox),
            tree: {
                ...parsedValue.tree,
                organisationNode: new Set(parsedValue.tree.organisationNode)
            }
        };

        return newSelectedFilterHighlightMap;
    },
    write: (value: SelectedFilterOptionHighlightMap) => {
        const copy: PersistedSelectedFilterOptionHighlightMap = {
            ...value,
            tree: {
                ...value.tree,
                organisationNode: Array.from(value.tree.organisationNode)
            }
        };

        return JSON.stringify(copy);
    },
};

/**
 * Type used for persisting the selected filter option map to session storage.
 */
type PersistedSelectedFilterOptionMap = Omit<SelectedFilterOptionMap, 'tree' | 'date'> & {
    tree: {
        organisationNode: string[]
    },
    date: {
        parentalLeaveStartDate: {
            after: string | null,
            before: string | null
        },
        salaryDate: {
            after: string | null,
            before: string | null
        }
    }
};

const filterMapSeralizer: Serializer<SelectedFilterOptionMap> = {
    read: (value): SelectedFilterOptionMap => {
        const parsedValue: PersistedSelectedFilterOptionMap = JSON.parse(value);

        const newSelectedFilterOptionMap: SelectedFilterOptionMap = {
            ...parsedValue,
            checkbox: withAllCheckboxFilterKeys(parsedValue.checkbox),
            date: {
                ...parsedValue.date,
                parentalLeaveStartDate: {
                    after: parsedValue.date.parentalLeaveStartDate.after != null ? new Date(parsedValue.date.parentalLeaveStartDate.after) : null,
                    before: parsedValue.date.parentalLeaveStartDate.before != null ? new Date(parsedValue.date.parentalLeaveStartDate.before) : null
                },
                salaryDate: {
                    after: parsedValue.date.salaryDate.after != null ? new Date(parsedValue.date.salaryDate.after) : null,
                    before: parsedValue.date.salaryDate.before != null ? new Date(parsedValue.date.salaryDate.before) : null
                }
            },
            tree: {
                ...parsedValue.tree,
                organisationNode: new Set(parsedValue.tree.organisationNode)
            }
        };

        return newSelectedFilterOptionMap;
    },
    write: (value: SelectedFilterOptionMap) => {
        const copy: PersistedSelectedFilterOptionMap = {
            ...value,
            tree: {
                ...value.tree,
                organisationNode: Array.from(value.tree.organisationNode)
            },
            date: {
                parentalLeaveStartDate: {
                    after: value.date.parentalLeaveStartDate.after?.toISOString() ?? null,
                    before: value.date.parentalLeaveStartDate.before?.toISOString() ?? null
                },
                salaryDate: {
                    after: value.date.salaryDate.after?.toISOString() ?? null,
                    before: value.date.salaryDate.before?.toISOString() ?? null
                }
            }
        };

        return JSON.stringify(copy);
    },
};

const _analyzeToolbarMessage = ref<ToolbarMessagePopup | null>(null);
const analyzeToolbarMessage = computed(() => _analyzeToolbarMessage.value);

function setAnalyzeToolbarMessage(value: ToolbarMessagePopup | null): void {
    _analyzeToolbarMessage.value = value;
}

/**
 * Transforms a range filter value to an EqualWorkFilter range value.
 * @param rangeFilter - The range filter to transform
 * @param min - The minimum value of the range filter
 * @param max - The maximum value of the range filter
 * @returns The transformed range filter or undefined if the range filter is invalid
 */
function transformRangeFilterValueToEqualWorkFilterValue(rangeFilter: [number, number] | null, min: number, max: number): { min: number, max: number } | undefined {
    // If range filter is unselected, return undefined
    // If the range filter is the same as the min and max, return undefined
    // if min and max are the same, we don't want to filter anything
    if (rangeFilter == null || (min === max) || (rangeFilter[0] === min && rangeFilter[1] === max)) {
        return undefined;
    }

    // If the range filter is invalid, return undefined
    if (rangeFilter[0] > rangeFilter[1]) {
        return undefined;
    }

    return {
        min: rangeFilter[0],
        max: rangeFilter[1]
    };
}

/**
 * Transforms a checkbox filter to an EqualWorkFilter checkbox
 * @param checkboxFilter - The checkbox filter to transform
 * @returns The transformed checkbox filter or undefined if the checkbox filter is invalid
 */
function transformCheckboxFilterToEqualWorkFilter(checkboxFilter: string[]): [string, ...string[]] | undefined {
    if (checkboxFilter.length === 0) {
        return undefined;
    }

    return checkboxFilter as [string, ...string[]];
}

/**
 * Transforms a count and highlight filter to an EqualWorkFilter count and highlight
 * @param array - The count and highlight filter to transform
 * @param activeKey - The active key of the count and highlight filter
 * @param negativeKey - The negative key of the count and highlight filter
 * @returns The transformed count and highlight filter or undefined
 */
function transformCountAndHighlightToEqualWorkFilterValue(array: string[], activeKey: string, negativeKey: string): boolean | undefined {
    // If both return undefined
    if (array.includes(activeKey) && array.includes(negativeKey)) {
        return undefined;
    }

    if (array.includes(activeKey)) {
        return true;
    }

    if (array.includes(negativeKey)) {
        return false;
    }

    return undefined;
}

/**
 * Transforms a tree filter to an EqualWorkFilter tree
 * @param tree - The tree filter to transform
 * @returns The transformed tree filter or undefined if the tree filter is empty
 */
function transformTreeFilterToEqualWorkFilter(tree: Set<string>): [string, ...string[]] | undefined {
    if (tree.size === 0) {
        return undefined;
    }

    return [...tree] as [string, ...string[]];
}

/**
 * Transforms a date filter to an EqualWorkFilter date
 * @param date - The date filter to transform
 * @param minDate - The minimum date of the date filter
 * @param maxDate - The maximum date of the date filter
 * @returns The transformed date filter or undefined if the date filter is invalid
 */
function transformDateFilterToEqualWorkFilter(date: { after: Date | null, before: Date | null }, minDate?: string, maxDate?: string): { min: string, max: string } | undefined {
    if (date.after == null && date.before == null) {
        return undefined;
    }

    // exit early if we don't have a min or max date
    if (minDate == null || maxDate == null) {
        return undefined;
    }

    return {
        min: date.after?.toISOString() ?? minDate,
        max: date.before?.toISOString() ?? maxDate
    };
}

/**
 * Checks if an array contains a specific id
 * @param array - The array to check
 * @param id - The id to check for
 * @returns true if the array contains the id, otherwise undefined
 */
function hasId(array: string[], id: string): true | undefined {
    if (array.includes(id)) {
        return true;
    }

    return undefined;
}

/**
 * Transforms a string array to a number array
 * @param array - The string array to transform
 * @returns The transformed number array or undefined if the array is null
 */
function transformStringArrayToNumberArray(array?: [string, ...string[]]): [number, ...number[]] | undefined {
    if (array == null || array.length === 0) {
        return undefined;
    }

    return array.map(i => parseInt(i)) as [number, ...number[]];
}

/**
 * Extracts the organization levels from a possible filter organisation node.
 *
 * @param node - The node to extract the organization levels from
 * @returns A list of organization levels
 */
function extractOrganizationLevels(node: PossibleFilterOrganisationNode | null): PossibleFilterOrganisationNode[][] {
    if (node == null) return [];

    const levels: PossibleFilterOrganisationNode[][] = [[node]];

    function traverse(currentLevel: PossibleFilterOrganisationNode[], depth: number): void {
        const nextLevel: PossibleFilterOrganisationNode[] = [];

        // Collect all children from current level
        currentLevel.forEach(levelNode => {
            nextLevel.push(...levelNode.children);
        });

        // If we found any children, add them to levels and continue traversing
        if (nextLevel.length > 0) {
            levels[depth] = nextLevel;
            traverse(nextLevel, depth + 1);
        }
    }

    traverse([node], 1);
    return levels;
}

/**
 * Creates a map of node IDs to highlight IDs.
 *
 * @param node - The node to create the map for
 * @param nodeIdHighlightMap - The map to store the node IDs and highlight IDs
 * @returns A map of node IDs to highlight IDs
 */
function createNodeIdHighlightMap(node: PossibleFilterOrganisationNode, nodeIdHighlightMap: Map<string, string[]> = new Map()): Map<string, string[]> {
    if (nodeIdHighlightMap.has(node.id) === false) {
        nodeIdHighlightMap.set(node.id, node.highlightIds);
    }

    for (const child of node.children) {
        createNodeIdHighlightMap(child, nodeIdHighlightMap);
    }

    return nodeIdHighlightMap;
}

/**
 * Extracts the organization nodes from the selected filter map
 * @param selectedFilterMap - The selected filter map
 * @returns A list of organization nodes
 */
function getOrganizationNodes(selectedFilterMap: SelectedFilterOptionMap): string[] {
    const treeNodes = transformTreeFilterToEqualWorkFilter(selectedFilterMap.tree.organisationNode) ?? [];

    const checkboxNodes = ORGANIZATION_LEVEL_KEYS.flatMap(key => transformCheckboxFilterToEqualWorkFilter(selectedFilterMap.checkbox[key]) ?? []
    );

    return Array.from(new Set([...treeNodes, ...checkboxNodes]));
}

function getOrganizationNodeFilter(selectedFilterMap: SelectedFilterOptionMap): [string, ...string[]] | undefined {
    const organizationNodes = getOrganizationNodes(selectedFilterMap);

    if (organizationNodes.length === 0) {
        return undefined;
    }

    return organizationNodes as [string, ...string[]];
}

export const useAnalyzeEqualWorkStore = defineStore(STORE_ID, () => {
    const { hidePerformance, hideSalary, hideName } = storeToRefs(usePresentationModeStore());

    /** States */
    const _analysisType = ref<AnalysisType>('predicted-salary');

    const _sideBarType = ref<SideBarType>('analyze');
    const _showSidebar = ref(false);
    const _previousSideBarType = ref<SideBarType>('analyze');
    const _analysisData = ref<EqualWorkAnalyzisDataV2 | null>(null);
    const _isLoadingAnalysisData = ref(false);
    const _isLoadingBulkChanges = ref(false);
    const _isLoadingEqualJob = ref(false);
    const currentComment = ref<string | null>(null);

    // Session stored for the selected employment retention feature
    // Need to know if swtiched to different job
    const _jobId = useDomainStorage<string | null>(() => 'pay-equity-audit-selected-job-id',
        null,
        sessionStorage
    );

    // Session stored for the selected employment retention
    const _selectedEmploymentId = useDomainStorage<string | null>(() => 'pay-equity-audit-selected-employment-id',
        null,
        sessionStorage
    );

    const _selectedEmployment = ref<EmploymentDetailV4 | null>(null);
    const _selectedEmploymentInsights = ref<InsightResponse | null>(null);

    const _analyzeJobEmployments = ref<AnalyzeJobEmploymentV2[] | null>(null);
    const _historicalAnalysis = ref<EmploymentHistoricalAnalysis | null>(null);
    const _workHistoricalAnalysis = ref<WorkHistoricalAnalysis | null>(null);
    const _selectedJob = ref<EqualWork | null>(null);
    const _insightVariables = ref<OrganizationEmploymentWorkInsightVariables | null>(null);
    const _possibleEqualWorkFilterValues = ref<PossibleEqualWorkFilterValues | null>(null);
    const _selectedPayRangeId = ref<string | null>(null);

    // Group comment draft for tracking current state of group analysis comment field
    const _groupCommentDraft = ref<string | null>(null);

    // We return a ref directly from this store to allow us to directly manipulate the data using
    // v-model. This is done because the type is too complex to support deep getter/setter methods
    // without making the code less readable.
    // This is here to allow us to store filter settings globally for the whole
    // analyze V2 module. It uses useStorage to store the data in the session storage.
    const selectedFilterHighlightMap = useDomainStorage<SelectedFilterOptionHighlightMap>(() => `pay-equity-audit-selected-filter-highlight-map-v2-${_jobId.value}`,
        getDefaultSelectedFiltersHightlight,
        sessionStorage,
        {
            serializer: filterHighlightMapSeralizer,
        }
    );

    // We return a ref directly from this store to allow us to directly manipulate the data using
    // v-model. This is done because the type is too complex to support deep getter/setter methods
    // without making the code less readable.
    // This is here to allow us to store filter highlight settings globally for the whole
    // analyze V2 module. It uses useStorage to store the data in the session storage.
    const selectedFilterMap = useDomainStorage<SelectedFilterOptionMap>(() => `pay-equity-audit-selected-filter-map-v2-${_jobId.value}`, getDefaultSelectedFilterOptionMap, sessionStorage, {
        serializer: filterMapSeralizer,
    });

    // Chart settings

    const _workTags = ref<WorkTag[] | null>(null);
    const _searchQuery = ref<string>('');

    // Settings for each analyzis type
    const _predictedSalarySettings = useDomainStorage<PredictedSalarySettings>('predicted-salary-settings', PREDICTED_SALARY_SETTINGS_DEFAULT, sessionStorage);
    const _structureAndExploreSettings = useDomainStorage<StructureSettings>('structure-and-explore-settings', STRUCTURE_AND_EXPLORE_SETTINGS_DEFAULT, sessionStorage);
    const _equalJobsSortBy = useDomainStorage<EqualJobsSortingGroup>('equal-jobs-sort-by', 'pay-gap', sessionStorage);

    /** Getters */
    const analysisType = computed<AnalysisType>(() => _analysisType.value);
    const sideBarType = computed<SideBarType | null>(() => _sideBarType.value);
    const jobId = computed<string | null>(() => _jobId.value);
    const analysisData = computed<EqualWorkAnalyzisDataV2 | null>(() => _analysisData.value);
    const selectedPayRangeId = computed<string | null>(() => _selectedPayRangeId.value);
    const isLoadingAnalysisData = computed<boolean>(() => _isLoadingAnalysisData.value);
    const isLoadingBulkChanges = computed<boolean>(() => _isLoadingBulkChanges.value);
    const isLoadingEqualJob = computed<boolean>(() => _isLoadingEqualJob.value);
    const insightVariables = computed<OrganizationEmploymentWorkInsightVariables | null>(() => _insightVariables.value);
    const isSimulating = computed<boolean>(() => (_predictedSalarySettings.value.querySettings.simulated ?? false) || (_structureAndExploreSettings.value.querySettings.simulated ?? false));

    const selectedEmploymentId = computed<string | null>(() => _selectedEmploymentId.value);
    const selectedEmployment = computed<EmploymentDetailV4 | null>(() => _selectedEmployment.value);
    const selectedEmploymentInsights = computed<InsightResponse | null>(() => _selectedEmploymentInsights.value);

    const analyzeJobEmployments = computed<AnalyzeJobEmploymentV2[] | null>(() => _analyzeJobEmployments.value);
    // Map of analyze job employment indexed by lowercased employment ID
    const employmentIdAnalyzeJobEmploymentMap = computed<Map<string, AnalyzeJobEmploymentV2>>(() => {
        if (analyzeJobEmployments.value == null) {
            return new Map();
        }

        const map = new Map<string, AnalyzeJobEmploymentV2>();

        for (const jobEmployment of analyzeJobEmployments.value) {
            map.set(jobEmployment.employmentId.toLowerCase(), jobEmployment);
        }

        return map;
    });
    const historicalAnalysis = computed<EmploymentHistoricalAnalysis | null>(() => _historicalAnalysis.value);
    const workHistoricalAnalysis = computed<WorkHistoricalAnalysis | null>(() => _workHistoricalAnalysis.value);
    const selectedJob = computed<EqualWork | null>(() => _selectedJob.value);
    const showSidebar = computed<boolean>(() => _showSidebar.value);
    const searchQuery = computed<string>(() => _searchQuery.value);
    const groupCommentDraft = computed<string | null>(() => _groupCommentDraft.value);
    const equalJobsSortBy = computed<EqualJobsSortingGroup>(() => _equalJobsSortBy.value);
    const filterQuery = computed<EqualWorkFilter | undefined>(() => {
        if (_possibleEqualWorkFilterValues.value == null) {
            return undefined;
        }

        // Transform the selected filter map to the EqualWorkFilter type
        const filter: EqualWorkFilter = {
            additionalDataOne: transformCheckboxFilterToEqualWorkFilter(selectedFilterMap.value.checkbox.additionalDataOne),
            additionalDataSecond: transformCheckboxFilterToEqualWorkFilter(selectedFilterMap.value.checkbox.additionalDataSecond),
            additionalDataThird: transformCheckboxFilterToEqualWorkFilter(selectedFilterMap.value.checkbox.additionalDataThird),
            adjustments: {
                frozen: transformCountAndHighlightToEqualWorkFilterValue(selectedFilterMap.value.countAndHighlightCheckbox.adjustments, 'frozen', 'notFrozen'),
                salaryIncrease: transformCountAndHighlightToEqualWorkFilterValue(selectedFilterMap.value.countAndHighlightCheckbox.adjustments, 'salaryIncrease', 'noSalaryIncrease'),
            },
            gender: transformStringArrayToNumberArray(transformCheckboxFilterToEqualWorkFilter(selectedFilterMap.value.checkbox.gender)),
            analyzed: transformCountAndHighlightToEqualWorkFilterValue(selectedFilterMap.value.countAndHighlightCheckbox.analyzeStatus, 'analysed', 'notAnalysed'),
            age: transformRangeFilterValueToEqualWorkFilterValue(selectedFilterMap.value.range.age, _possibleEqualWorkFilterValues.value.age.min, _possibleEqualWorkFilterValues.value.age.max),
            bonusActualBonus: transformRangeFilterValueToEqualWorkFilterValue(selectedFilterMap.value.range.bonusActualBonus, _possibleEqualWorkFilterValues.value.bonusActualBonus.min, _possibleEqualWorkFilterValues.value.bonusActualBonus.max),
            bonusBenefitsInKind: transformRangeFilterValueToEqualWorkFilterValue(selectedFilterMap.value.range.bonusBenefitsInKind, _possibleEqualWorkFilterValues.value.bonusBenefitsInKind.min, _possibleEqualWorkFilterValues.value.bonusBenefitsInKind.max),
            bonusOneTimeBonus: transformRangeFilterValueToEqualWorkFilterValue(selectedFilterMap.value.range.bonusOneTimeBonus, _possibleEqualWorkFilterValues.value.bonusOneTimeBonus.min, _possibleEqualWorkFilterValues.value.bonusOneTimeBonus.max),
            bonusTargetBonus: transformRangeFilterValueToEqualWorkFilterValue(selectedFilterMap.value.range.bonusTargetBonus, _possibleEqualWorkFilterValues.value.bonusTargetBonus.min, _possibleEqualWorkFilterValues.value.bonusTargetBonus.max),
            companyCarAllowance: transformRangeFilterValueToEqualWorkFilterValue(selectedFilterMap.value.range.companyCarAllowance, _possibleEqualWorkFilterValues.value.companyCarAllowance.min, _possibleEqualWorkFilterValues.value.companyCarAllowance.max),
            contract: transformCheckboxFilterToEqualWorkFilter(selectedFilterMap.value.checkbox.contract),
            equity: transformRangeFilterValueToEqualWorkFilterValue(selectedFilterMap.value.range.equity, _possibleEqualWorkFilterValues.value.equity.min, _possibleEqualWorkFilterValues.value.equity.max),
            comparatio: transformRangeFilterValueToEqualWorkFilterValue(selectedFilterMap.value.range.comparatio, _possibleEqualWorkFilterValues.value.comparatio.min, _possibleEqualWorkFilterValues.value.comparatio.max),
            employmentRate: transformRangeFilterValueToEqualWorkFilterValue(selectedFilterMap.value.range.employmentRate, _possibleEqualWorkFilterValues.value.employmentRate.min, _possibleEqualWorkFilterValues.value.employmentRate.max),
            employmentTime: transformRangeFilterValueToEqualWorkFilterValue(selectedFilterMap.value.range.employmentTime, _possibleEqualWorkFilterValues.value.employmentTime.min, _possibleEqualWorkFilterValues.value.employmentTime.max),
            formOfEmployment: transformCheckboxFilterToEqualWorkFilter(selectedFilterMap.value.checkbox.formOfEmployment),
            explainations: {
                competingLabourMarket: hasId(selectedFilterMap.value.countAndHighlightCheckbox.explainations, 'competingLabourMarket'),
                lessCompetence: hasId(selectedFilterMap.value.countAndHighlightCheckbox.explainations, 'lessCompetence'),
                lessExperience: hasId(selectedFilterMap.value.countAndHighlightCheckbox.explainations, 'lessExperience'),
                lessPerformance: hasId(selectedFilterMap.value.countAndHighlightCheckbox.explainations, 'lessPerformance'),
                lessResponsibility: hasId(selectedFilterMap.value.countAndHighlightCheckbox.explainations, 'lessResponsibility'),
                market: hasId(selectedFilterMap.value.countAndHighlightCheckbox.explainations, 'market'),
                prevEmployment: hasId(selectedFilterMap.value.countAndHighlightCheckbox.explainations, 'prevEmployment'),
                other: hasId(selectedFilterMap.value.countAndHighlightCheckbox.explainations, 'other'),
                noExplaination: hasId(selectedFilterMap.value.countAndHighlightCheckbox.explainations, 'noExplaination'),
                moreCompetence: hasId(selectedFilterMap.value.countAndHighlightCheckbox.explainations, 'moreCompetence'),
                moreExperience: hasId(selectedFilterMap.value.countAndHighlightCheckbox.explainations, 'moreExperience'),
                morePerformance: hasId(selectedFilterMap.value.countAndHighlightCheckbox.explainations, 'morePerformance'),
                moreResponsibility: hasId(selectedFilterMap.value.countAndHighlightCheckbox.explainations, 'moreResponsibility'),
            },
            holidaysPaidHoliday: transformRangeFilterValueToEqualWorkFilterValue(selectedFilterMap.value.range.holidaysPaidHoliday, _possibleEqualWorkFilterValues.value.holidaysPaidHoliday.min, _possibleEqualWorkFilterValues.value.holidaysPaidHoliday.max),
            holidaysPaidSickDays: transformRangeFilterValueToEqualWorkFilterValue(selectedFilterMap.value.range.holidaysPaidSickDays, _possibleEqualWorkFilterValues.value.holidaysPaidSickDays.min, _possibleEqualWorkFilterValues.value.holidaysPaidSickDays.max),
            legalEntity: transformCheckboxFilterToEqualWorkFilter(selectedFilterMap.value.checkbox.legalEntity),
            locality: transformCheckboxFilterToEqualWorkFilter(selectedFilterMap.value.checkbox.locality),
            managerCode: transformCheckboxFilterToEqualWorkFilter(selectedFilterMap.value.checkbox.managerCode),
            pensionOccupationalPension: transformRangeFilterValueToEqualWorkFilterValue(selectedFilterMap.value.range.pensionOccupationalPension, _possibleEqualWorkFilterValues.value.pensionOccupationalPension.min, _possibleEqualWorkFilterValues.value.pensionOccupationalPension.max),
            position: transformCheckboxFilterToEqualWorkFilter(selectedFilterMap.value.checkbox.position),
            positionLevel: transformCheckboxFilterToEqualWorkFilter(selectedFilterMap.value.checkbox.positionLevel),
            localCareerBand: transformCheckboxFilterToEqualWorkFilter(selectedFilterMap.value.checkbox.localCareerBand),
            localJobCode: transformCheckboxFilterToEqualWorkFilter(selectedFilterMap.value.checkbox.localJobCode),
            localJobFamily: transformCheckboxFilterToEqualWorkFilter(selectedFilterMap.value.checkbox.localJobFamily),
            localJobProfile: transformCheckboxFilterToEqualWorkFilter(selectedFilterMap.value.checkbox.localJobProfile),
            localJobProfileLevel: transformCheckboxFilterToEqualWorkFilter(selectedFilterMap.value.checkbox.localJobProfileLevel),
            localSubFamily: transformCheckboxFilterToEqualWorkFilter(selectedFilterMap.value.checkbox.localSubFamily),
            statutoryGrade: transformCheckboxFilterToEqualWorkFilter(selectedFilterMap.value.checkbox.statutoryGrade),
            occupationalClassification: transformCheckboxFilterToEqualWorkFilter(selectedFilterMap.value.checkbox.occupationalClassification),
            salaryYear: transformStringArrayToNumberArray(transformCheckboxFilterToEqualWorkFilter(selectedFilterMap.value.checkbox.salaryYear)),
            statisticalcode: transformCheckboxFilterToEqualWorkFilter(selectedFilterMap.value.checkbox.statisticalcode),
            salary: transformRangeFilterValueToEqualWorkFilterValue(selectedFilterMap.value.range.salary, _possibleEqualWorkFilterValues.value.salary.min, _possibleEqualWorkFilterValues.value.salary.max),
            salaryIncrement: transformRangeFilterValueToEqualWorkFilterValue(selectedFilterMap.value.range.salaryIncrement, _possibleEqualWorkFilterValues.value.salaryIncrement.min, _possibleEqualWorkFilterValues.value.salaryIncrement.max),
            salaryIncrementSecond: transformRangeFilterValueToEqualWorkFilterValue(selectedFilterMap.value.range.salaryIncrementSecond, _possibleEqualWorkFilterValues.value.salaryIncrementSecond.min, _possibleEqualWorkFilterValues.value.salaryIncrementSecond.max),
            salaryIncrementThird: transformRangeFilterValueToEqualWorkFilterValue(selectedFilterMap.value.range.salaryIncrementThird, _possibleEqualWorkFilterValues.value.salaryIncrementThird.min, _possibleEqualWorkFilterValues.value.salaryIncrementThird.max),
            organisationNode: getOrganizationNodeFilter(selectedFilterMap.value),
            parentalLeaveStartDate: transformDateFilterToEqualWorkFilter(selectedFilterMap.value.date.parentalLeaveStartDate, _possibleEqualWorkFilterValues.value.parentalLeaveStartDate?.minDate, _possibleEqualWorkFilterValues.value.parentalLeaveStartDate?.maxDate),
            salaryDate: transformDateFilterToEqualWorkFilter(selectedFilterMap.value.date.salaryDate, _possibleEqualWorkFilterValues.value.salaryDate?.minDate, _possibleEqualWorkFilterValues.value.salaryDate?.maxDate),
            performance: transformRangeFilterValueToEqualWorkFilterValue(selectedFilterMap.value.range.performance, _possibleEqualWorkFilterValues.value.performance.min, _possibleEqualWorkFilterValues.value.performance.max),
            salaryExchange: transformRangeFilterValueToEqualWorkFilterValue(selectedFilterMap.value.range.salaryExchange, _possibleEqualWorkFilterValues.value.salaryExchange.min, _possibleEqualWorkFilterValues.value.salaryExchange.max),
            standardDeviationDistance: transformRangeFilterValueToEqualWorkFilterValue(selectedFilterMap.value.range.standardDeviationDistance, _possibleEqualWorkFilterValues.value.standardDeviationDistance.min, _possibleEqualWorkFilterValues.value.standardDeviationDistance.max),
            timeInCurrentPosition: transformRangeFilterValueToEqualWorkFilterValue(selectedFilterMap.value.range.timeInCurrentPosition, _possibleEqualWorkFilterValues.value.timeInCurrentPosition.min, _possibleEqualWorkFilterValues.value.timeInCurrentPosition.max),
            totalSalary: transformRangeFilterValueToEqualWorkFilterValue(selectedFilterMap.value.range.totalSalary, _possibleEqualWorkFilterValues.value.totalSalary.min, _possibleEqualWorkFilterValues.value.totalSalary.max),
            overtime: transformRangeFilterValueToEqualWorkFilterValue(selectedFilterMap.value.range.overtime, _possibleEqualWorkFilterValues.value.overtime.min, _possibleEqualWorkFilterValues.value.overtime.max),
            insights: {
                beenOnParentalLeave: hasId(selectedFilterMap.value.countAndHighlightCheckbox.insights, 'beenOnParentalLeave'),
                differentSalaryYear: hasId(selectedFilterMap.value.countAndHighlightCheckbox.insights, 'differentSalaryYear'),
                highPerformer: hasId(selectedFilterMap.value.countAndHighlightCheckbox.insights, 'highPerformer'),
                lowPerformer: hasId(selectedFilterMap.value.countAndHighlightCheckbox.insights, 'lowPerformer'),
                manager: hasId(selectedFilterMap.value.countAndHighlightCheckbox.insights, 'manager'),
                newToJob: hasId(selectedFilterMap.value.countAndHighlightCheckbox.insights, 'newToJob'),
                older: hasId(selectedFilterMap.value.countAndHighlightCheckbox.insights, 'older'),
                receivedBonus: hasId(selectedFilterMap.value.countAndHighlightCheckbox.insights, 'receivedBonus'),
                younger: hasId(selectedFilterMap.value.countAndHighlightCheckbox.insights, 'younger'),
                outlierAbove: hasId(selectedFilterMap.value.countAndHighlightCheckbox.insights, 'outlierAbove'),
                outlierBelow: hasId(selectedFilterMap.value.countAndHighlightCheckbox.insights, 'outlierBelow'),
                flaggedForAnalysis: hasId(selectedFilterMap.value.countAndHighlightCheckbox.insights, 'flaggedForAnalysis'),
            },
            costCenter: transformCheckboxFilterToEqualWorkFilter(selectedFilterMap.value.checkbox.costCenter),
        };

        return filter;
    });

    const presentationMode = computed<Pick<EqualWorkQueryOptions, 'presentationModeName' | 'presentationModePerformance' | 'presentationModeSalary'>>(() => ({
        presentationModeName: hideName.value,
        presentationModePerformance: hidePerformance.value,
        presentationModeSalary: hideSalary.value,
    }));
    const predictedSalaryEqualWorkQueryOptions = computed<EqualWorkQueryOptions>(() => ({ ..._predictedSalarySettings.value.querySettings, ...presentationMode.value, filter: filterQuery.value }));

    const structureAndExploreEqualWorkQueryOptions = computed<EqualWorkQueryOptions>(() => ({ ..._structureAndExploreSettings.value.querySettings, ...presentationMode.value, filter: filterQuery.value }));

    const predictedSalarySettings = computed<PredictedSalarySettings>(() => _predictedSalarySettings.value);
    const structureAndExploreSettings = computed<StructureSettings>(() => _structureAndExploreSettings.value);
    const showDistanceToAverage = computed<boolean>(() => _structureAndExploreSettings.value.querySettings.distanceTo != null);
    const isUnableToCalculateModel = computed<boolean | null>(() => {
        return analyzeJobEmployments.value != null ? analyzeJobEmployments.value.every(item => item.standardDeviation == null) : null;
    });
    const employmentsWithoutStandardDeviation = computed<AnalyzeJobEmploymentV2[]>(() => analyzeJobEmployments.value?.filter(x => x.standardDeviation == null) ?? []);
    const workTags = computed<WorkTag[] | null>(() => _workTags.value);
    const workTagsMap = computed<Map<string, WorkTag> | null>(() => (_workTags.value != null ? transformWorkTagsToMap(_workTags.value) : null));
    const possibleEqualWorkFilterValues = computed<PossibleEqualWorkFilterValues | null>(() => _possibleEqualWorkFilterValues.value);

    const extractedOrganizationLevels = computed<PossibleFilterOrganisationNode[][]>(() => extractOrganizationLevels(possibleEqualWorkFilterValues.value?.organisationNode?.tree ?? null));

    const hasPayRanges = computed<boolean>(() => {
        const paybandMax = _analysisData.value?.keyvalues.paybandMax;
        const paybandMin = _analysisData.value?.keyvalues.paybandMin;
        return paybandMax?.type === 'value' || paybandMin?.type === 'value';
    });

    const hasBenchmark = computed<boolean>(() => {
        const benchmarkUpper = _analysisData.value?.benchmark?.upper;
        const benchmarkMiddle = _analysisData.value?.benchmark?.middle;
        const benchmarkLower = _analysisData.value?.benchmark?.lower;

        return benchmarkUpper?.type === 'value' && benchmarkUpper.value !== 0
        || benchmarkMiddle?.type === 'value' && benchmarkMiddle.value !== 0
        || benchmarkLower?.type === 'value' && benchmarkLower.value !== 0;
    });

    const organizationLevelCheckboxFilterItemMap = computed<Record<OrganizationLevelCheckboxFilterKey, CheckboxFilterItem[]>>(() => {
        const levels = extractedOrganizationLevels.value;
        const result: Partial<Record<OrganizationLevelCheckboxFilterKey, CheckboxFilterItem[]>> = {};

        // Ensure we always have 6 levels, even if empty
        for (let i = 0; i < 6; i++) {
            const levelNodes = levels[i] ?? [];
            result[ORGANIZATION_LEVEL_KEYS[i]] = levelNodes.map(levelNode => ({
                id: levelNode.id,
                label: `${levelNode.name} (${levelNode.count})`,
                disabled: levelNode.count === 0,
            })).filter(node => !node.disabled);
        }

        return result as Record<OrganizationLevelCheckboxFilterKey, CheckboxFilterItem[]>;
    });

    const organizationLevelPossibleFilterDisplayStringCheckboxValuesMap = computed<Record<OrganizationLevelCheckboxFilterKey, PossibleFilterDisplayStringCheckboxValues>>(() => {
        const levels = extractedOrganizationLevels.value;
        const result: Partial<Record<OrganizationLevelCheckboxFilterKey, PossibleFilterDisplayStringCheckboxValues>> = {};

        for (let i = 0; i < 6; i++) {
            const levelNodes = levels[i] ?? [];

            // Ensure we always have 6 levels, even if empty
            result[ORGANIZATION_LEVEL_KEYS[i]] = levelNodes.map(levelNode => ({
                value: levelNode.id,
                display: levelNode.name,
                count: levelNode.count,
                highlightIds: levelNode.highlightIds
            }));
        }

        return result as Record<OrganizationLevelCheckboxFilterKey, PossibleFilterDisplayStringCheckboxValues>;
    });

    const filterHighlightEmploymentIds = computed<string[] | null>(() => {
        if (possibleEqualWorkFilterValues.value == null) {
            return null;
        }

        // counter for how many unique filters are applied not the number of values selected in those filters
        let filterHits = 0;
        // counters for all ids to see in how many each id is hit only the ids that match the number of filters in the end gets highlighted
        const idHits: Map<string, number> = new Map<string, number>();
        const addEmploymentId = (employmentId: string): void => {
            if (idHits.has(employmentId)) {
                idHits.set(employmentId, (idHits.get(employmentId) ?? 1) + 1);
            } else {
                idHits.set(employmentId, 1);
            }
        };

        let hasEncounteredOrganizationLevelFilter = false;

        Object.entries(selectedFilterHighlightMap.value.checkbox).forEach(([key, value]) => {
            if (value == null || possibleEqualWorkFilterValues.value == null) {
                return;
            }

            // Exit early if no highlight values are selected
            if (value.length === 0) {
                return;
            }

            // Find and highlight IDs for the specific key
            if (isOrganizationLevelCheckboxFilterKey(key)) {
                // We treat all organization level filters as a single filter when calculating which employments
                // to highlight. Because highlight IDs never overlap between organization level filters, as values are
                // mutually exclusive, we can be sure that each employment ID in the idHits map is only incremented
                // once after going through all organization level filters.
                if (hasEncounteredOrganizationLevelFilter === false) {
                    filterHits += 1;
                    hasEncounteredOrganizationLevelFilter = true;
                }

                // Handle organization level filters
                organizationLevelPossibleFilterDisplayStringCheckboxValuesMap.value[key].filter(option => value.includes(option.value)).flatMap(option => option.highlightIds).forEach(employmentId => {
                    addEmploymentId(employmentId);
                });
            } else {
                filterHits += 1;
                // Handle other filters
                // Statutory grade and occupational classification are optional in the possible values
                (possibleEqualWorkFilterValues.value[key as BackendCheckboxFilterKey] ?? []).filter(filter => value.includes(filter.value.toString())).flatMap(filter => filter.highlightIds).forEach(employmentId => {
                    addEmploymentId(employmentId);
                });
            }
        });

        // Handle count and highlight checkbox
        Object.entries(selectedFilterHighlightMap.value.countAndHighlightCheckbox).forEach(([key, value]) => {
            if (value == null || possibleEqualWorkFilterValues.value == null) {
                return;
            }

            if (key === 'insights') {
                // Special handling for insights since each value should be treated as a seperate filter and not a value in a filter
                const idsPerInsightFilter = Object.entries(possibleEqualWorkFilterValues.value[key as CountAndHighlightCheckboxFilterKey]).filter(([key1]) => value.includes(key1)).map(([, val1]) => val1.highlightIds);

                for (const ids of idsPerInsightFilter) {
                    if (ids.length > 0) filterHits += 1;
                    ids.forEach(employmentId => {
                        addEmploymentId(employmentId);
                    });
                }
            } else {
                // Find highlight IDs for the specific key
                if (value.length > 0) filterHits += 1;
                // We treat them as one filter but because employments can show up in multiple filter values
                // we need to only add them once, otherwise the filter hit count won't match.
                const uniqueHighlightIds = new Set(Object.entries(possibleEqualWorkFilterValues.value[key as CountAndHighlightCheckboxFilterKey]).filter(([key1]) => value.includes(key1)).flatMap(([, val1]) => val1.highlightIds));
                uniqueHighlightIds.forEach(employmentId => {
                    addEmploymentId(employmentId);
                });
            }
        });

        // Handle tree
        Object.entries(selectedFilterHighlightMap.value.tree).forEach(([key, value]) => {
            if (value == null || possibleEqualWorkFilterValues.value == null) {
                return;
            }

            if (value.size > 0) filterHits += 1;

            const relevantTree = possibleEqualWorkFilterValues.value[key as TreeFilterKey].tree;

            if (relevantTree == null) {
                return;
            }

            const nodeIdHighlightMap = createNodeIdHighlightMap(relevantTree);

            value.forEach(i => {
                const ids = nodeIdHighlightMap.get(i);
                if (ids != null && ids.length > 0) {
                    ids.forEach(id => {
                        addEmploymentId(id);
                    });
                }
            });
        });

        if (filterHits === 0) {
            return null;
        }

        // Only select the ids that are in all selected filters
        const idsToHighlight: string[] = [];
        for (const [id, count] of idHits.entries()) {
            if (count === filterHits) {
                idsToHighlight.push(id);
            }
        }
        return idsToHighlight;
    });

    const searchHighlightEmploymentIds = computed<string[]>(() => {
        if (searchQuery.value === '' || analyzeJobEmployments.value == null) {
            return [];
        }

        return analyzeJobEmployments.value.filter(employment => employment.name.type === 'value' && employment.name.value.toLowerCase().includes(searchQuery.value.toLowerCase())).map(employment => employment.employmentId);
    });

    const idsToHighlight = computed<string[] | null>(() => {
        const highlightedIds = new Set<string>();

        if (filterHighlightEmploymentIds.value !== null) {
            filterHighlightEmploymentIds.value.forEach(id => {
                highlightedIds.add(id);
            });
        }

        searchHighlightEmploymentIds.value.forEach(id => {
            highlightedIds.add(id);
        });

        return (highlightedIds.size > 0 || filterHighlightEmploymentIds.value !== null) ? Array.from(highlightedIds) : null;
    });

    // check if the chart type is set to line (aggregated data) and if there are any highlights selected
    const isLineChartAndHighlightActive = computed<boolean>(() => {
        const activeHighlightsArray = selectedFilterHighlightMap.value.countAndHighlightCheckbox.insights;
        const isLineChart = _structureAndExploreSettings.value.chartType === 'line';
        return isLineChart && activeHighlightsArray.length > 0;
    });

    const employmentsShownInScatterPlot = computed(() => _analysisData.value?.scatterPlotData?.flatMap(group => group.employments.map(x => x.id)) ?? []);

    const predictedSalariesMap = computed<Map<string, number>>(() => {
        const map = new Map<string, number>();
        _analysisData.value?.scatterPlotData.forEach(group => group.employments.forEach(employment => map.set(employment.id.toLowerCase(), employment.predictedSalary.type === 'value' ? employment.predictedSalary.value : 0)));
        return map;
    });

    const historicalSalaryAdjustments = computed((): EmploymentHistoricalSalaryAdjustment[] => {
        if (historicalAnalysis.value == null) return [];
        return historicalAnalysis.value.historicalSalaryAdjustments.filter(adjustment => adjustment.salaryAdjustmentYear0 !== 0 || adjustment.salaryAdjustmentYear1 !== 0 || adjustment.salaryAdjustmentYear2 !== 0);
    });

    /** Actions */

    function setAnalysisType(type: AnalysisType): void {
        _analysisType.value = type;
    }

    function togglePreviousSideBarType(): void {
        _sideBarType.value = _previousSideBarType.value;
        _previousSideBarType.value = 'analyze';

        setShowSidebar(false);
    }

    function setSideBarType(type: SideBarType): void {
        if (_sideBarType.value !== 'explore-settings') {
            _previousSideBarType.value = _sideBarType.value;
        }

        _sideBarType.value = type;
    }
    function setJobId(id: string): void {
        if (_jobId.value !== id) {
            setSelectedEmploymentId(null);
            setSelectedPayRangeId(null);
        }
        _jobId.value = id;
    }

    function setSelectedPayRangeId(id: string | null): void {
        _selectedPayRangeId.value = id;
    }

    function setAnalysisData(data: EqualWorkAnalyzisDataV2 | null): void {
        _analysisData.value = data;
    }

    function setIsLoadingAnalysisData(isLoading: boolean): void {
        _isLoadingAnalysisData.value = isLoading;
    }

    function setIsLoadingBulkChanges(isLoading: boolean): void {
        _isLoadingBulkChanges.value = isLoading;
    }

    function setIsLoadingEqualJob(isLoading: boolean): void {
        _isLoadingEqualJob.value = isLoading;
    }

    function setAnalyzeJobEmployments(employments: AnalyzeJobEmploymentV2[] | null): void {
        _analyzeJobEmployments.value = employments;
    }

    function setHistoricalAnalysis(data: EmploymentHistoricalAnalysis | null): void {
        _historicalAnalysis.value = data;
    }

    function setWorkHistoricalAnalysis(data: WorkHistoricalAnalysis | null): void {
        _workHistoricalAnalysis.value = data;
    }

    function setShowSidebar(show: boolean): void {
        _showSidebar.value = show;
    }

    function setSelectedEmploymentId(id: string | null): void {
        _selectedEmploymentId.value = id;
    }

    function setSelectedEmployment(employment: EmploymentDetailV4 | null): void {
        _selectedEmployment.value = employment;
    }

    function setSelectedEmploymentInsights(insights: InsightResponse | null): void {
        _selectedEmploymentInsights.value = insights;
    }

    function setSelectedJob(job: EqualWork | null): void {
        _selectedJob.value = job;
    }

    function setEqualJobsSortBy(sortBy: EqualJobsSortingGroup): void {
        _equalJobsSortBy.value = sortBy;
    }

    function setXAxis(xAxis: InputFieldNames.age | InputFieldNames.employmentTime | InputFieldNames.performance | InputFieldNames.timeInCurrentPosition | undefined): void {
        _structureAndExploreSettings.value.querySettings.xAxis = xAxis;
    }

    function setSalaryType(salaryType: SalaryType): void {
        _structureAndExploreSettings.value.querySettings.salaryType = salaryType;
    }

    function setGroupBy(groupBy: EqualWorkQueryOptions['groupBy'] | null): void {
        if (groupBy === null) {
            _structureAndExploreSettings.value.querySettings.groupBy = undefined;
        } else {
            _structureAndExploreSettings.value.querySettings.groupBy = groupBy;
        }
    }

    function toggleSimulatedSalarySettings(value: boolean): void {
        _structureAndExploreSettings.value.querySettings.simulated = value;
        _predictedSalarySettings.value.querySettings.simulated = value;
    }

    function setChartType(type: ChartType): void {
        _structureAndExploreSettings.value.chartType = type;
    }

    function setShowGroupMeanSalaryLine(show: boolean): void {
        _structureAndExploreSettings.value.showGroupMeanSalaryLine = show;
    }

    function setShowGroupMedianSalaryLine(show: boolean): void {
        _structureAndExploreSettings.value.showGroupMedianSalaryLine = show;
    }

    function setShowBenchmark(show: boolean): void {
        _structureAndExploreSettings.value.showBenchmark = show;
    }

    function setShowRegression(show: boolean): void {
        _structureAndExploreSettings.value.showRegression = show;
    }

    function setDistanceTo(distanceTo: EqualWorkQueryOptions['distanceTo']): void {
        _structureAndExploreSettings.value.querySettings.distanceTo = distanceTo;
    }

    function toggleShowPredictedPayRange(): void {
        _predictedSalarySettings.value.payRange = !_predictedSalarySettings.value.payRange;
    }

    function toggleDistanceToAverage(show: boolean): void {
        _structureAndExploreSettings.value.querySettings.distanceTo = show ? 'mean' : undefined;
    }

    function toggleShowExplorePayRange(): void {
        _structureAndExploreSettings.value.payRange = !_structureAndExploreSettings.value.payRange;
    }

    /**
     * Sets which work tags are available for the job.
     * @param tags The work tags to set.
     */
    function setWorkTags(tags: WorkTag[] | null): void {
        _workTags.value = tags;
    }

    function setInsightVariables(insights: OrganizationEmploymentWorkInsightVariables | null): void {
        _insightVariables.value = insights;
    }

    function setPossibleEqualWorkFilterValues(values: PossibleEqualWorkFilterValues | null): void {
        _possibleEqualWorkFilterValues.value = values;
    }

    function setGroupCommentDraft(comment: string | null): void {
        _groupCommentDraft.value = comment;
    }

    function getDefaultSelectedFiltersHightlight(): SelectedFilterOptionHighlightMap {
        return {
            checkbox: {
                additionalDataOne: [],
                additionalDataSecond: [],
                additionalDataThird: [],
                contract: [],
                costCenter: [],
                legalEntity: [],
                localCareerBand: [],
                localJobCode: [],
                localJobFamily: [],
                localJobProfile: [],
                localJobProfileLevel: [],
                localSubFamily: [],
                statutoryGrade: [],
                occupationalClassification: [],
                managerCode: [],
                position: [],
                positionLevel: [],
                formOfEmployment: [],
                statisticalcode: [],
                salaryYear: [],
                gender: [],
                locality: [],
                organizationLevelOne: [],
                organizationLevelTwo: [],
                organizationLevelThree: [],
                organizationLevelFour: [],
                organizationLevelFive: [],
                organizationLevelSix: [],
            },
            countAndHighlightCheckbox: {
                adjustments: [],
                analyzeStatus: [],
                explainations: [],
                insights: [],
            },
            tree: {
                organisationNode: new Set()
            }
        };
    }

    function getDefaultSelectedFilterOptionMap(): SelectedFilterOptionMap {
        return {
            countAndHighlightCheckbox: {
                insights: [],
                adjustments: [],
                analyzeStatus: [],
                explainations: []
            },
            checkbox: {
                localCareerBand: [],
                gender: [],
                locality: [],
                formOfEmployment: [],
                positionLevel: [],
                position: [],
                statisticalcode: [],
                salaryYear: [],
                additionalDataOne: [],
                additionalDataSecond: [],
                additionalDataThird: [],
                costCenter: [],
                contract: [],
                legalEntity: [],
                localJobCode: [],
                localJobFamily: [],
                localJobProfile: [],
                localJobProfileLevel: [],
                localSubFamily: [],
                statutoryGrade: [],
                occupationalClassification: [],
                managerCode: [],
                organizationLevelOne: [],
                organizationLevelTwo: [],
                organizationLevelThree: [],
                organizationLevelFour: [],
                organizationLevelFive: [],
                organizationLevelSix: [],
            },
            range: {
                salary: null,
                bonusActualBonus: null,
                bonusBenefitsInKind: null,
                companyCarAllowance: null,
                pensionOccupationalPension: null,
                age: null,
                salaryExchange: null,
                salaryIncrement: null,
                salaryIncrementSecond: null,
                salaryIncrementThird: null,
                equity: null,
                totalSalary: null,
                comparatio: null,
                holidaysPaidHoliday: null,
                bonusOneTimeBonus: null,
                overtime: null,
                holidaysPaidSickDays: null,
                bonusTargetBonus: null,
                employmentRate: null,
                timeInCurrentPosition: null,
                employmentTime: null,
                performance: null,
                standardDeviationDistance: null,
                yearlyPayouts: null,
                fteWorkHoursPerMonth: null,
            },
            date: {
                salaryDate: {
                    after: null,
                    before: null
                },
                parentalLeaveStartDate: {
                    after: null,
                    before: null
                }
            },
            tree: {
                organisationNode: new Set()
            }
        };
    }

    /**
     * Reset the selected filters to their default state
     */
    function resetSelectedFilters(): void {
        selectedFilterHighlightMap.value = getDefaultSelectedFiltersHightlight();
        selectedFilterMap.value = getDefaultSelectedFilterOptionMap();
    }

    function setSearchQuery(query: string): void {
        _searchQuery.value = query;
    }

    return {
        /** States */
        selectedFilterHighlightMap,
        selectedFilterMap,
        /** Getters */
        searchQuery,
        analysisType,
        sideBarType,
        jobId,
        analysisData,
        selectedPayRangeId,
        isLoadingAnalysisData,
        isLoadingBulkChanges,
        isLoadingEqualJob,
        isSimulating,
        analyzeJobEmployments,
        employmentIdAnalyzeJobEmploymentMap,
        historicalAnalysis,
        workHistoricalAnalysis,
        selectedEmploymentId,
        selectedEmployment,
        selectedEmploymentInsights,
        selectedJob,
        showSidebar,
        predictedSalarySettings,
        structureAndExploreSettings,
        showDistanceToAverage,
        hasPayRanges,
        hasBenchmark,
        isUnableToCalculateModel,
        idsToHighlight,
        workTags,
        workTagsMap,
        predictedSalariesMap,
        insightVariables,
        historicalSalaryAdjustments,
        possibleEqualWorkFilterValues,
        employmentsShownInScatterPlot,
        organizationLevelCheckboxFilterItemMap,
        organizationLevelPossibleFilterDisplayStringCheckboxValuesMap,
        filterHighlightEmploymentIds,
        searchHighlightEmploymentIds,
        predictedSalaryEqualWorkQueryOptions,
        structureAndExploreEqualWorkQueryOptions,
        filterQuery,
        analyzeToolbarMessage,
        isLineChartAndHighlightActive,
        groupCommentDraft,
        presentationMode,
        equalJobsSortBy,
        currentComment,
        /** Actions */
        setAnalysisType,
        setSideBarType,
        setJobId,
        setAnalysisData,
        setIsLoadingAnalysisData,
        setIsLoadingBulkChanges,
        setAnalyzeJobEmployments,
        setHistoricalAnalysis,
        setWorkHistoricalAnalysis,
        setGroupBy,
        setShowSidebar,
        togglePreviousSideBarType,
        setSelectedEmploymentId,
        setSelectedPayRangeId,
        setSelectedEmployment,
        setSelectedEmploymentInsights,
        setSelectedJob,
        setEqualJobsSortBy,
        setXAxis,
        setSalaryType,
        toggleSimulatedSalarySettings,
        setChartType,
        setShowGroupMeanSalaryLine,
        setShowGroupMedianSalaryLine,
        setShowBenchmark,
        setDistanceTo,
        toggleDistanceToAverage,
        setWorkTags,
        toggleShowPredictedPayRange,
        toggleShowExplorePayRange,
        setInsightVariables,
        setPossibleEqualWorkFilterValues,
        setSearchQuery,
        setIsLoadingEqualJob,
        resetSelectedFilters,
        setAnalyzeToolbarMessage,
        setGroupCommentDraft,
        setShowRegression,
        employmentsWithoutStandardDeviation
    };
});
